import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { AtsCandidateSourceCategoriesEnum } from 'generated/resources/datatable.graphql'
import { isEmpty, omitBy } from 'lodash'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import type { FormErrors } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateCandidateSources } from 'modules/ats/hooks/candidateSources/useCreateCandidateSources'
import { candidateSourceCategoryOptions } from 'modules/ats/lib/utils/candidateSources'

interface NewCandidateSourceFormValues {
  category: string
  name: string
  editable: boolean
}

interface Props {
  onClose: () => void
}

export const NewCandidateSourceModal = ({ onClose }: Props) => {
  const { create } = useCreateCandidateSources()
  const { company } = useCurrentSession()

  const form = useForm<NewCandidateSourceFormValues>({
    defaultValues: {
      name: '',
      category: '',
      editable: true,
    },
    validate: (data) => {
      const errors: FormErrors<NewCandidateSourceFormValues> = {}

      if (!data.category) {
        errors.category = i18n.t('common.empty')
      }

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await create({
        ...omitBy(data, isEmpty),
        companyId: company.id,
        name: data.name,
        category: data.category as AtsCandidateSourceCategoriesEnum,
        editable: data.editable,
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('ats.manage_sources_customization.create_modal.title')}
      description={i18n.t('ats.manage_sources_customization.create_modal.description')}
      illustration="document-edit"
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={form.isSubmitting} submit>
            {i18n.t('ats.manage_sources_customization.create_modal.create_button')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <Select
          none={i18n.t('common.select')}
          label={i18n.t('ats.manage_sources_customization.create_modal.category_label')}
          options={candidateSourceCategoryOptions()}
          value={form.values.category}
          error={form.touched.category ? form.errors.category : undefined}
          onChange={(value) => form.setValues({ ...form.values, category: value })}
          mandatory
        />
        <TextField
          label={i18n.t('ats.manage_sources_customization.create_modal.name_label')}
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.touched.name ? form.errors.name : undefined}
          mandatory
        />
      </Box>
    </Modal>
  )
}
